import { useEffect, useRef, useState, useCallback } from 'react';
import axios from 'axios';
import { ChatCircleDots, PaperPlaneRight, X, ArrowLeft, Phone, Lightning, CaretRight } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Standard quick reply suggestions for students. Each option can have an `action`
// that triggers a side-effect (open a modal in the dashboard, scroll, etc.).
// - 'prefill'      → just fill the input so the student can complete it (e.g. add a location)
// - 'send_action'  → send the message, close the chat, then trigger an app event
// - 'send'         → just send the message
const CANCEL_24H_NOTE =
    '\n\nLet op: als ik niet minstens 24 uur van tevoren afzeg, telt deze les volgens mijn lespakket als een gereden les.';

const STUDENT_QUICK_REPLIES = [
    {
        id: 'cancel',
        emoji: '🚫',
        label: 'Les annuleren',
        body: 'Hoi! Ik wil graag mijn aankomende les annuleren.' + CANCEL_24H_NOTE,
        action: 'send_action',
        event: 'qr:open-cancel',
        toast: 'Open het annuleer-scherm…',
    },
    {
        id: 'sick',
        emoji: '🤒',
        label: 'Ik ben ziek',
        body: 'Hoi! Ik voel me niet goed en kan helaas niet komen rijden vandaag.' + CANCEL_24H_NOTE,
        action: 'send_action',
        event: 'qr:open-cancel',
        toast: 'Open het annuleer-scherm…',
    },
    {
        id: 'reschedule',
        emoji: '📅',
        label: 'Les verplaatsen',
        body: 'Hoi! Ik wil graag mijn les verplaatsen. Zou het kunnen op: \n• Dag/datum: \n• Tijd: ',
        action: 'prefill',
    },
    {
        id: 'pickup',
        emoji: '📍',
        label: 'Ophaal-locatie',
        body: 'Hoi! Kun je me hier ophalen: ',
        action: 'prefill',
    },
    {
        id: 'lessons-left',
        emoji: '🔢',
        label: 'Hoeveel lessen heb ik nog?',
        body: 'Hoi! Hoeveel lessen heb ik nog over in mijn pakket?',
        action: 'send_action',
        event: 'qr:show-lessons',
        toast: 'Je lessen-overzicht staat bovenaan in je leskaart.',
    },
    {
        id: 'buy-more',
        emoji: '💳',
        label: 'Extra lessen kopen',
        body: 'Hoi! Ik zou graag extra lessen bijkopen. Welke mogelijkheden zijn er?',
        action: 'send_action',
        event: 'qr:show-payment',
        toast: 'Bekijk je betalingsplan in de carousel.',
    },
    {
        id: 'refer-friend',
        emoji: '🎁',
        label: 'Iemand aanmelden (gratis les)',
        body: '',
        action: 'open_only',
        event: 'qr:open-referral',
        toast: 'Open het Gratis-les formulier…',
    },
    {
        id: 'running-late',
        emoji: '⏰',
        label: 'Ik ben iets later',
        body: 'Hoi! Ik ben helaas iets later dan afgesproken. Sorry!',
        action: 'send',
    },
    {
        id: 'thanks',
        emoji: '🙏',
        label: 'Bedankt voor de les!',
        body: 'Bedankt voor de les! 🙏',
        action: 'send',
    },
];

function formatTime(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        return d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
    } catch { return ''; }
}
function formatDay(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const yesterday = new Date(today.getTime() - 86400000);
        const md = new Date(d); md.setHours(0, 0, 0, 0);
        if (md.getTime() === today.getTime()) return 'Vandaag';
        if (md.getTime() === yesterday.getTime()) return 'Gisteren';
        return d.toLocaleDateString('nl-NL', { weekday: 'long', day: 'numeric', month: 'long' });
    } catch { return ''; }
}

function MessageThread({ studentId, role, headerTitle, onBack, onPhone, onClose }) {
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState('');
    const [sending, setSending] = useState(false);
    const [showChips, setShowChips] = useState(true);
    const scrollerRef = useRef(null);
    const inputRef = useRef(null);

    const params = studentId ? { params: { student_id: studentId } } : {};

    const load = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/chat/messages`, { ...params, withCredentials: true });
            setMessages(data);
        } catch (e) { /* ignore */ }
        // Mark read after loading
        try { await axios.post(`${API}/chat/messages/read`, null, { ...params, withCredentials: true }); } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [studentId]);

    useEffect(() => {
        load();
        const t = setInterval(load, 5000);
        return () => clearInterval(t);
    }, [load]);

    useEffect(() => {
        if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }, [messages.length]);

    const postMessage = async (body) => {
        try {
            const { data } = await axios.post(`${API}/chat/messages`, { body }, { ...params, withCredentials: true });
            setMessages((m) => [...m, data]);
            return data;
        } catch (err) { return null; }
    };

    const send = async (e) => {
        e?.preventDefault?.();
        const body = draft.trim();
        if (!body || sending) return;
        setSending(true);
        await postMessage(body);
        setDraft('');
        setSending(false);
    };

    const handleQuickReply = async (qr) => {
        if (sending) return;
        // 1) Pre-fill into the input so the student can complete (e.g. add address)
        if (qr.action === 'prefill') {
            setDraft(qr.body);
            setShowChips(false);
            // Focus and place caret at end so they can keep typing
            window.setTimeout(() => {
                const el = inputRef.current;
                if (el) {
                    el.focus();
                    const v = el.value;
                    el.setSelectionRange(v.length, v.length);
                }
            }, 50);
            return;
        }
        // 2) Open-only flows (no message sent, just trigger the relevant UI)
        if (qr.action === 'open_only') {
            if (qr.toast) toast.success(qr.toast);
            if (qr.event) window.dispatchEvent(new CustomEvent(qr.event));
            onClose?.();
            return;
        }
        // 3) Send + optional action
        setSending(true);
        await postMessage(qr.body);
        setSending(false);
        if (qr.action === 'send_action') {
            if (qr.toast) toast.success(qr.toast);
            if (qr.event) {
                // Tiny delay so the message bubble lands first
                window.setTimeout(() => window.dispatchEvent(new CustomEvent(qr.event)), 250);
            }
            // Close the chat so the user lands in the relevant tab/modal
            window.setTimeout(() => onClose?.(), 350);
        }
    };

    let lastDay = '';
    return (
        <div className="flex flex-col h-full">
            {/* Header */}
            <div className="px-4 py-3 border-b border-[#4A5568] flex items-center gap-2 bg-[#1A202C] flex-shrink-0">
                {onBack && (
                    <button onClick={onBack} className="text-gray-400 hover:text-white p-1 -m-1" data-testid="chat-back">
                        <ArrowLeft size={20} />
                    </button>
                )}
                <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-white truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>{headerTitle}</div>
                    <div className="text-[10px] text-gray-500">Berichten worden direct doorgegeven</div>
                </div>
                {onPhone && (
                    <a href={`tel:${onPhone}`} className="text-gray-400 hover:text-[#D4A537] p-1.5" title="Bellen" data-testid="chat-call">
                        <Phone size={18} />
                    </a>
                )}
            </div>

            {/* Messages */}
            <div ref={scrollerRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-1.5 bg-[#0e131c]" data-testid="chat-messages">
                {messages.length === 0 && (
                    <div className="text-center text-xs text-gray-500 mt-8 px-6 leading-relaxed">
                        Nog geen berichten. Stuur het eerste bericht hieronder.
                    </div>
                )}
                {messages.map((m) => {
                    const isMe = m.sender_role === role;
                    const day = formatDay(m.created_at);
                    const showDay = day !== lastDay;
                    lastDay = day;
                    return (
                        <div key={m.id}>
                            {showDay && (
                                <div className="text-center my-3">
                                    <span className="text-[10px] uppercase tracking-widest text-gray-500 bg-[#1A202C] px-2 py-0.5 rounded-full">
                                        {day}
                                    </span>
                                </div>
                            )}
                            <div className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[78%] rounded-2xl px-3 py-2 text-sm ${isMe
                                    ? 'bg-[#D4A537] text-[#1A202C] rounded-br-sm'
                                    : 'bg-[#2D3748] text-gray-100 rounded-bl-sm border border-[#4A5568]'}`}
                                    data-testid={`chat-bubble-${m.id}`}>
                                    <div className="whitespace-pre-wrap break-words">{m.body}</div>
                                    <div className={`text-[10px] mt-0.5 ${isMe ? 'text-[#1A202C]/70' : 'text-gray-500'} text-right`}>
                                        {formatTime(m.created_at)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Quick replies (students only) */}
            {role === 'student' && showChips && (
                <div className="border-t border-[#4A5568] bg-[#1A202C] px-2.5 pt-2 pb-1 flex-shrink-0" data-testid="quick-replies-bar">
                    <div className="flex items-center justify-between mb-1.5 px-1">
                        <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-gray-500">
                            <Lightning size={11} weight="fill" className="text-[#D4A537]" />
                            <span>Snelle vragen</span>
                        </div>
                        <button
                            onClick={() => setShowChips(false)}
                            className="text-[10px] text-gray-500 hover:text-gray-300"
                            data-testid="quick-replies-hide"
                        >
                            Verbergen
                        </button>
                    </div>
                    <div className="flex gap-1.5 overflow-x-auto pb-1.5 -mx-0.5 px-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                        {STUDENT_QUICK_REPLIES.map((qr) => (
                            <button
                                key={qr.id}
                                onClick={() => handleQuickReply(qr)}
                                disabled={sending}
                                className="flex-shrink-0 inline-flex items-center gap-1.5 bg-[#2D3748] hover:bg-[#3D4A5C] border border-[#4A5568] hover:border-[#D4A537]/60 active:scale-[0.97] text-gray-200 text-xs rounded-full px-3 py-1.5 transition-all disabled:opacity-50"
                                data-testid={`quick-reply-${qr.id}`}
                            >
                                <span aria-hidden="true">{qr.emoji}</span>
                                <span>{qr.label}</span>
                                {(qr.action === 'send_action' || qr.action === 'open_only') && (
                                    <CaretRight size={10} className="text-gray-500" />
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            )}
            {role === 'student' && !showChips && (
                <div className="border-t border-[#4A5568] bg-[#1A202C] px-2.5 py-1 flex-shrink-0">
                    <button
                        onClick={() => setShowChips(true)}
                        className="text-[10px] text-gray-400 hover:text-[#D4A537] inline-flex items-center gap-1"
                        data-testid="quick-replies-show"
                    >
                        <Lightning size={11} weight="fill" />
                        Toon snelle vragen
                    </button>
                </div>
            )}

            {/* Composer */}
            <form onSubmit={send} className={`${role === 'student' ? '' : 'border-t border-[#4A5568]'} bg-[#1A202C] p-2.5 flex items-end gap-2 flex-shrink-0`}>
                <textarea
                    ref={inputRef}
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
                    }}
                    placeholder="Typ een bericht..."
                    rows={1}
                    className="flex-1 resize-none bg-[#2D3748] border border-[#4A5568] rounded-xl px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:border-[#D4A537] max-h-32"
                    data-testid="chat-input"
                />
                <button
                    type="submit"
                    disabled={!draft.trim() || sending}
                    className="bg-[#D4A537] hover:bg-[#E5B84A] disabled:opacity-40 disabled:cursor-not-allowed text-[#1A202C] rounded-xl p-2.5 transition-colors flex-shrink-0"
                    data-testid="chat-send"
                    aria-label="Verstuur"
                >
                    <PaperPlaneRight size={18} weight="fill" />
                </button>
            </form>
        </div>
    );
}

function ConversationList({ onPick }) {
    const [convs, setConvs] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let live = true;
        const load = async () => {
            try {
                const { data } = await axios.get(`${API}/chat/conversations`, { withCredentials: true });
                if (live) setConvs(data);
            } catch { /* ignore */ }
            if (live) setLoading(false);
        };
        load();
        const t = setInterval(load, 5000);
        return () => { live = false; clearInterval(t); };
    }, []);

    return (
        <div className="flex flex-col h-full">
            <div className="px-4 py-3 border-b border-[#4A5568] bg-[#1A202C] flex-shrink-0">
                <div className="text-sm font-semibold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Berichten</div>
                <div className="text-[10px] text-gray-500">Kies een leerling om te chatten</div>
            </div>
            <div className="flex-1 overflow-y-auto">
                {loading && <div className="p-6 text-center text-xs text-gray-500">Laden...</div>}
                {!loading && convs.length === 0 && (
                    <div className="p-6 text-center text-xs text-gray-500">Nog geen leerlingen om mee te chatten.</div>
                )}
                {convs.map((c) => (
                    <button
                        key={c.student_id}
                        onClick={() => onPick(c)}
                        className="w-full text-left px-4 py-3 border-b border-[#4A5568]/40 hover:bg-[#3D4A5C]/40 transition-colors flex items-start gap-3"
                        data-testid={`conv-row-${c.student_id}`}
                    >
                        <div className="w-10 h-10 rounded-full bg-[#D4A537]/15 border border-[#D4A537]/40 flex items-center justify-center text-[#D4A537] font-bold flex-shrink-0">
                            {(c.student_name || '?').slice(0, 1).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-baseline justify-between gap-2">
                                <span className="text-sm font-medium text-white truncate">{c.student_name}</span>
                                {c.last_message && (
                                    <span className="text-[10px] text-gray-500 flex-shrink-0">
                                        {formatTime(c.last_message.created_at)}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center justify-between gap-2 mt-0.5">
                                <span className={`text-xs truncate ${c.unread_count > 0 ? 'text-white font-medium' : 'text-gray-400'}`}>
                                    {c.last_message
                                        ? (c.last_message.sender_role === 'instructor' ? 'Jij: ' : '')
                                        + c.last_message.body
                                        : <em className="text-gray-500">Geen berichten</em>}
                                </span>
                                {c.unread_count > 0 && (
                                    <span className="bg-[#D4A537] text-[#1A202C] text-[10px] font-bold rounded-full px-1.5 min-w-[18px] h-[18px] flex items-center justify-center flex-shrink-0">
                                        {c.unread_count}
                                    </span>
                                )}
                            </div>
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
}

export default function ChatModal({ open, onClose, role, currentStudentName }) {
    const [selectedConv, setSelectedConv] = useState(null);

    useEffect(() => {
        if (!open) setSelectedConv(null);
    }, [open]);

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
            <DialogContent className="bg-[#2D3748] border-[#4A5568] text-white p-0 max-w-md h-[80vh] sm:h-[600px] flex flex-col overflow-hidden" data-testid="chat-modal">
                <DialogTitle className="sr-only">Chat</DialogTitle>
                {role === 'instructor' && !selectedConv ? (
                    <ConversationList onPick={setSelectedConv} />
                ) : (
                    <MessageThread
                        studentId={role === 'instructor' ? selectedConv?.student_id : undefined}
                        role={role}
                        headerTitle={role === 'instructor' ? (selectedConv?.student_name || '') : 'Instructeur'}
                        onBack={role === 'instructor' ? () => setSelectedConv(null) : undefined}
                        onPhone={role === 'instructor' ? selectedConv?.student_phone : null}
                        onClose={onClose}
                    />
                )}
                <button
                    onClick={onClose}
                    className="absolute top-3 right-3 text-gray-400 hover:text-white p-1 z-10"
                    data-testid="chat-close"
                    aria-label="Sluiten"
                >
                    <X size={18} />
                </button>
            </DialogContent>
        </Dialog>
    );
}

/** Header button with unread badge — opens the chat modal. */
export function ChatButton({ role }) {
    const [open, setOpen] = useState(false);
    const [unread, setUnread] = useState(0);

    useEffect(() => {
        let live = true;
        const load = async () => {
            try {
                const { data } = await axios.get(`${API}/chat/unread-count`, { withCredentials: true });
                if (live) setUnread(data.unread || 0);
            } catch { /* ignore */ }
        };
        load();
        const t = setInterval(load, 15000);
        return () => { live = false; clearInterval(t); };
    }, [open]);

    // Allow opening the chat via a custom event (used by PWA shortcuts & quick replies)
    useEffect(() => {
        const handler = () => setOpen(true);
        window.addEventListener('qr:open-chat', handler);
        return () => window.removeEventListener('qr:open-chat', handler);
    }, []);

    return (
        <>
            <button
                onClick={() => setOpen(true)}
                className="relative text-white hover:text-[#D4A537] transition-colors p-2 rounded-lg hover:bg-[#3D4A5C]"
                title="Chat"
                data-testid="chat-open-button"
                aria-label="Open chat"
            >
                <ChatCircleDots size={20} weight="fill" />
                {unread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 bg-[#D4A537] text-[#1A202C] text-[10px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1">
                        {unread > 9 ? '9+' : unread}
                    </span>
                )}
            </button>
            <ChatModal open={open} onClose={() => setOpen(false)} role={role} />
        </>
    );
}
